import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft, Check, X, Trash2, Clock, UserCheck, UserX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved'

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, userName) => {
    setProcessing(prev => ({ ...prev, [userId]: 'approving' }));
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} er nu godkendt`);
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error('Kunne ikke godkende bruger');
    } finally {
      setProcessing(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleRejectUser = async (userId, userName) => {
    setProcessing(prev => ({ ...prev, [userId]: 'rejecting' }));
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} er afvist`);
      fetchUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      toast.error('Kunne ikke afvise bruger');
    } finally {
      setProcessing(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Er du sikker på, at du vil slette bruger ${userEmail}? Denne handling kan ikke fortrydes.`)) {
      return;
    }

    setProcessing(prev => ({ ...prev, [userId]: 'deleting' }));
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bruger slettet');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke slette bruger');
    } finally {
      setProcessing(prev => ({ ...prev, [userId]: null }));
    }
  };

  const filteredUsers = users
    .filter(u => {
      if (filter === 'pending') return u.approved === false;
      if (filter === 'approved') return u.approved === true || u.approved === undefined;
      return true;
    })
    // Sort alphabetically by first name
    .sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'da'); // Danish locale for proper sorting
    });

  const pendingCount = users.filter(u => u.approved === false).length;
  const approvedCount = users.filter(u => u.approved === true || u.approved === undefined).length;

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div className="admin-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem' }}>
          {/* Header */}
          <div style={{ 
            background: 'white', 
            padding: '1rem', 
            borderRadius: '12px', 
            marginBottom: '1rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: '0.75rem',
                padding: '0.25rem',
                borderRadius: '5px'
              }}
            >
              <ArrowLeft size={18} />
              Tilbage
            </button>
            <h1 className="booking-title" style={{ marginBottom: '0.25rem', fontSize: '1.5rem' }}>
              Brugeradministration
            </h1>
            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Godkend nye brugere og administrer eksisterende
            </p>
            
            {/* Filter Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setFilter('all')}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: filter === 'all' ? '#7c6f5e' : '#f5f5f5',
                  color: filter === 'all' ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '0.85rem'
                }}
              >
                Alle ({users.length})
              </button>
              <button
                onClick={() => setFilter('pending')}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: filter === 'pending' ? '#ff9800' : '#fff3e0',
                  color: filter === 'pending' ? 'white' : '#f57c00',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.85rem'
                }}
              >
                <Clock size={14} />
                Afventer ({pendingCount})
              </button>
              <button
                onClick={() => setFilter('approved')}
                style={{
                  padding: '0.5rem 0.75rem',
                  background: filter === 'approved' ? '#4caf50' : '#e8f5e9',
                  color: filter === 'approved' ? 'white' : '#2e7d32',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.85rem'
                }}
              >
                <Check size={14} />
                Godkendt ({approvedCount})
              </button>
            </div>
          </div>

          {/* Pending Users Alert */}
          {pendingCount > 0 && filter !== 'approved' && (
            <div style={{
              background: '#fff3e0',
              border: '1px solid #ffe082',
              padding: '1rem 1.5rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Clock size={20} color="#f57c00" />
              <span style={{ color: '#e65100' }}>
                <strong>{pendingCount}</strong> bruger{pendingCount !== 1 ? 'e' : ''} afventer godkendelse
              </span>
            </div>
          )}

          {/* Users List */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}>
            {loading ? (
              <div style={{ padding: '2rem' }}>
                <LoadingSkeleton type="list" count={5} />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
                {filter === 'pending' ? 'Ingen brugere afventer godkendelse' : 'Ingen brugere fundet'}
              </div>
            ) : (
              <div>
                {filteredUsers.map((u, index) => {
                  const isApproved = u.approved === true || u.approved === undefined;
                  const isAdmin = ADMIN_EMAILS.includes(u.email);
                  const isProcessing = processing[u.id];
                  
                  return (
                    <div
                      key={u.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1rem',
                        borderBottom: index < filteredUsers.length - 1 ? '1px solid #eee' : 'none',
                        background: !isApproved ? '#fffbf5' : 'white',
                        gap: '0.75rem'
                      }}
                    >
                      {/* Top row: Name and badges */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '0.5rem'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '0.25rem',
                          minWidth: 0,
                          flex: 1
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            flexWrap: 'wrap'
                          }}>
                            <span style={{ 
                              fontWeight: 600, 
                              color: '#333',
                              fontSize: '1rem',
                              wordBreak: 'break-word'
                            }}>
                              {u.name}
                            </span>
                            {isAdmin && (
                              <span style={{
                                padding: '0.15rem 0.4rem',
                                background: '#e3f2fd',
                                color: '#1976d2',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap'
                              }}>
                                Admin
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: '0.85rem', 
                            color: '#666',
                            wordBreak: 'break-all'
                          }}>
                            {u.email}
                          </div>
                          {u.phone && (
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {u.phone}
                            </div>
                          )}
                          <div style={{ fontSize: '0.75rem', color: '#999' }}>
                            Oprettet: {new Date(u.created_at).toLocaleDateString('da-DK', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                        
                        {/* Status badge */}
                        <div>
                          {isApproved ? (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: '#e8f5e9',
                              color: '#2e7d32',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              whiteSpace: 'nowrap'
                            }}>
                              <UserCheck size={12} />
                              Godkendt
                            </span>
                          ) : (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: '#fff3e0',
                              color: '#f57c00',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              whiteSpace: 'nowrap'
                            }}>
                              <Clock size={12} />
                              Afventer
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Action buttons - always visible */}
                      <div style={{ 
                        display: 'flex', 
                        gap: '0.5rem',
                        flexWrap: 'wrap'
                      }}>
                        {!isApproved && !isAdmin && (
                          <button
                            onClick={() => handleApproveUser(u.id, u.name)}
                            disabled={isProcessing}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: '0.5rem 0.75rem',
                              background: isProcessing === 'approving' ? '#a5d6a7' : '#4caf50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: isProcessing ? 'wait' : 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              flex: '1',
                              justifyContent: 'center',
                              minWidth: '100px'
                            }}
                          >
                            <Check size={16} />
                            {isProcessing === 'approving' ? 'Godkender...' : 'Godkend'}
                          </button>
                        )}
                        
                        {isApproved && !isAdmin && (
                          <button
                            onClick={() => handleRejectUser(u.id, u.name)}
                            disabled={isProcessing}
                            title="Fjern godkendelse"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: '0.5rem 0.75rem',
                              background: '#f5f5f5',
                              color: '#666',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: isProcessing ? 'wait' : 'pointer',
                              fontSize: '0.85rem',
                              flex: '1',
                              justifyContent: 'center',
                              minWidth: '100px'
                            }}
                          >
                            <UserX size={16} />
                            Fjern godkendelse
                          </button>
                        )}
                        
                        {!isAdmin && user?.email !== u.email && (
                          <button
                            onClick={() => handleDeleteUser(u.id, u.email)}
                            disabled={isProcessing}
                            title="Slet bruger"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.4rem',
                              padding: '0.5rem 0.75rem',
                              background: '#ffebee',
                              color: '#c62828',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: isProcessing ? 'wait' : 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            <Trash2 size={16} />
                            Slet
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: '#e3f2fd',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#1565c0'
          }}>
            <strong>Sådan virker det:</strong> Nye brugere skal godkendes før de kan booke undervisning. 
            Klik "Godkend" for at give adgang til booking-systemet.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
